/**
 * Helpers for GPI multi-intake feed lists.
 * Settings (volume/hour/…): [solid, liquid1, liquid2, liquid3] (indices 0–3).
 * FEED_TOTAL f101: [liquid1, liquid2, liquid3] only — solid stays on s105 / hopper.
 * Liquid pumps in commands/UI: 1–3.
 */

export const FEED_SOLID = 0;
export const FEED_LIQUID1 = 1;
export const FEED_LIQUID2 = 2;
export const FEED_LIQUID3 = 3;

export const FEED_SLOT_LABELS = ['solid', 'liquid1', 'liquid2', 'liquid3'];

/** True when firmware sent a JSON list (new multi-feed firmware). */
export function isFeedListValue(raw) {
    if (Array.isArray(raw)) {
        return true;
    }
    if (typeof raw !== 'string') {
        return false;
    }
    const trimmed = raw.trim();
    if (!trimmed.startsWith('[')) {
        return false;
    }
    try {
        return Array.isArray(JSON.parse(trimmed));
    } catch {
        return false;
    }
}

/**
 * Detect multi-intake firmware from raw slow/fast payload indices.
 * Older GPI keeps scalars — do not treat those as multi-feed.
 */
export function isMultiFeedFirmwarePayload(slowOrFastData, { checkFastTotalizer = false } = {}) {
    if (!slowOrFastData) {
        return false;
    }
    const slowIndexes = [12, 30, 35, 47, 73];
    if (slowIndexes.some((i) => isFeedListValue(slowOrFastData[i]))) {
        return true;
    }
    if (checkFastTotalizer && isFeedListValue(slowOrFastData[101])) {
        return true;
    }
    return false;
}

/**
 * Parse a firmware list value (JSON array string or already-array) into a 4-slot array.
 * Scalar fallback wraps as liquid1: [null, value, null, null].
 */
export function parseFeedList(raw, { scalarAsLiquid1 = true } = {}) {
    if (raw == null || raw === '' || raw === 'xx') {
        return [null, null, null, null];
    }

    if (Array.isArray(raw)) {
        return normalizeFeedList(raw);
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                    return normalizeFeedList(parsed);
                }
            } catch {
                // fall through to scalar
            }
        }
    }

    if (scalarAsLiquid1) {
        return [null, raw, null, null];
    }
    return [raw, null, null, null];
}

function normalizeFeedList(arr) {
    return [0, 1, 2, 3].map((i) => (arr[i] !== undefined ? arr[i] : null));
}

/**
 * Parse f101 FEED_TOTAL → canonical [null, l1, l2, l3] (FEED_SELECT 1–3 aligned).
 * Firmware sends [liquid1, liquid2, liquid3].
 */
export function parseFeedTotalList(raw) {
    if (raw == null || raw === '' || raw === 'xx') {
        return [null, null, null, null];
    }

    let arr = null;
    if (Array.isArray(raw)) {
        arr = [...raw];
    } else if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                    arr = [...parsed];
                }
            } catch {
                // fall through
            }
        }
    }

    if (arr == null) {
        return [null, raw, null, null];
    }

    if (arr.length === 1) {
        return [null, arr[0] ?? null, null, null];
    }
    if (arr.length === 3) {
        return [null, arr[0] ?? null, arr[1] ?? null, arr[2] ?? null];
    }
    // 4+: [solid, l1, l2, l3] — solid unused for liquid BGP sum
    return [0, 1, 2, 3].map((i) => (arr[i] !== undefined ? arr[i] : null));
}

/** Read one slot from a list or scalar field. */
export function feedListAt(raw, index, { scalarAsLiquid1 = true } = {}) {
    const list = parseFeedList(raw, { scalarAsLiquid1 });
    return list[index] ?? null;
}

/** FEED_AUTO bitmask: bit0=pump1, bit1=pump2, bit2=pump3. */
export function isPumpAutoOn(mask, liquidPump /* 1-3 */) {
    const n = Number(mask);
    if (!Number.isFinite(n) || liquidPump < 1 || liquidPump > 3) {
        return false;
    }
    // Legacy bool 0/1: treat as pump 1 only
    if (n === 0 || n === 1) {
        return liquidPump === 1 ? n === 1 : false;
    }
    return (n & (1 << (liquidPump - 1))) !== 0;
}

export function setPumpAutoBit(mask, liquidPump /* 1-3 */, on) {
    let n = Number(mask);
    if (!Number.isFinite(n) || n < 0) {
        n = 0;
    }
    // Upgrade legacy bool to bitmask for pump 1
    if (n === 1 && liquidPump !== 1) {
        n = 0b001;
    }
    const bit = 1 << (liquidPump - 1);
    return on ? (n | bit) : (n & ~bit);
}

/** How many liquid intake pumps to show (1–3). Invalid/legacy values → 1. */
export function resolveNoFeeds(noFeeds) {
    const n = Number(noFeeds);
    if (!Number.isFinite(n) || n < 1) {
        return 1;
    }
    // Old firmware reused s65 for compressor motor minutes (often >> 3)
    if (n > 3) {
        return 1;
    }
    return Math.floor(n);
}

/** Sum liquid slots of FEED_TOTAL ([l1,l2,l3] → canonical indices 1–3) for BGP. */
export function sumLiquidFeedTotal(raw) {
    const list = parseFeedTotalList(raw);
    return [1, 2, 3].reduce((sum, i) => {
        const v = Number(list[i]);
        return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
}

/** FEED_TOTAL value for liquid pump 1–3. */
export function feedTotalAtPump(raw, feedSelect) {
    if (feedSelect < 1 || feedSelect > 3) {
        return null;
    }
    return parseFeedTotalList(raw)[feedSelect] ?? null;
}

/** Hour options 0–23.75 step 0.25 for FEED_HOUR dropdowns. */
export function feedHourOptions() {
    const opts = [];
    for (let h = 0; h <= 23.75 + 1e-9; h += 0.25) {
        const value = Math.round(h * 100) / 100;
        const hours = Math.floor(value);
        const minutes = Math.round((value - hours) * 60);
        opts.push({
            value: String(value),
            label: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
        });
    }
    return opts;
}

export function formatFeedHour(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return '-';
    }
    const hours = Math.floor(n);
    const minutes = Math.round((n - hours) * 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
